import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type OAuthClient, type OAuthClientCreated } from "../lib/api.ts";
import { useStaff } from "../lib/staff.ts";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";

export function StaffOAuthClientsPage() {
  const qc = useQueryClient();
  // Mutations (create / update / delete) are admin-only on the server.
  // Support + developer get a read-only view of the registered clients.
  const { role } = useStaff();
  const isAdmin  = role === "admin";
  const list = useQuery({
    queryKey: ["staff", "oauth-clients"],
    queryFn:  api.staffListOAuthClients,
  });
  const sortable = useSortableTable(list.data?.clients ?? [], {
    name:         (c) => c.name,
    clientId:     (c) => c.clientId,
    creatorEmail: (c) => c.creatorEmail,
    createdAt:    (c) => c.createdAt,
    disabled:     (c) => c.disabled ? 1 : 0,
  }, "createdAt", "desc");

  // --- Create -------------------------------------------------------------
  const [name, setName]          = useState("");
  const [redirects, setRedirects] = useState("");
  const [createErr, setCreateErr] = useState<string | null>(null);
  // The plain client_secret is shown exactly once, after registration.
  const [justCreated, setJustCreated] = useState<OAuthClientCreated | null>(null);

  const create = useMutation({
    mutationFn: () => api.staffCreateOAuthClient(
      name.trim(),
      redirects.split(/\s+|,/).map((s) => s.trim()).filter(Boolean),
    ),
    onSuccess: (data) => {
      setJustCreated(data);
      setName("");
      setRedirects("");
      setCreateErr(null);
      qc.invalidateQueries({ queryKey: ["staff", "oauth-clients"] });
    },
    onError: (e: Error) => setCreateErr(e.message),
  });

  // --- Edit / delete -------------------------------------------------------
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName]   = useState("");
  const [editUrls, setEditUrls]   = useState("");

  const update = useMutation({
    mutationFn: (vars: { id: string; patch: { name?: string; redirectUrls?: string[]; disabled?: boolean } }) =>
      api.staffUpdateOAuthClient(vars.id, vars.patch),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ["staff", "oauth-clients"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.staffDeleteOAuthClient(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["staff", "oauth-clients"] }),
  });

  return (
    <>
      <h1>OAuth clients</h1>
      <p className="muted">
        Cloud-issued OIDC clients for staff-side tools (Grafana on the office Synology,
        future staff dashboards). Sign-in goes through this cloud, with staff_role
        forwarded as a claim so the client can map it to its own roles.
      </p>

      {isAdmin && (
      <section className="card" style={{ marginBottom: "1.5rem" }}>
        <h3>Register a new client</h3>
        {justCreated ? (
          <NewClientSecret created={justCreated} onDismiss={() => setJustCreated(null)} />
        ) : (
          <form
            className="formstack"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <label>
              Name
              <input
                placeholder="Grafana — Synology"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label>
              Redirect URIs (one per line)
              <textarea
                rows={3}
                placeholder="http://astore.local:3000/login/generic_oauth"
                required
                value={redirects}
                onChange={(e) => setRedirects(e.target.value)}
              />
            </label>
            {createErr && <div className="err">{createErr}</div>}
            <button type="submit" disabled={create.isPending}>
              {create.isPending ? "Registering…" : "Register client"}
            </button>
          </form>
        )}
      </section>
      )}

      {list.isLoading && <p className="muted">Loading…</p>}
      {list.error && <p className="err">Couldn't load — {(list.error as Error).message}</p>}
      {list.data && (
        <table>
          <thead>
            <tr>
              <SortHeader label="Name"         columnKey="name"         sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Client ID"    columnKey="clientId"     sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <th>Redirect URIs</th>
              <SortHeader label="Created by"   columnKey="creatorEmail" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Created"      columnKey="createdAt"    sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Status"       columnKey="disabled"     sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortable.sorted.map((c) => (
              <ClientRow
                key={c.id}
                client={c}
                canMutate={isAdmin}
                editing={editingId === c.id}
                editName={editName}
                editUrls={editUrls}
                onStartEdit={() => {
                  setEditingId(c.id);
                  setEditName(c.name);
                  setEditUrls(c.redirectUrls.join("\n"));
                }}
                onCancelEdit={() => setEditingId(null)}
                onChangeName={setEditName}
                onChangeUrls={setEditUrls}
                onSaveEdit={() => update.mutate({
                  id: c.id,
                  patch: {
                    name: editName.trim(),
                    redirectUrls: editUrls.split(/\s+|,/).map((u) => u.trim()).filter(Boolean),
                  },
                })}
                onToggleDisabled={() => update.mutate({ id: c.id, patch: { disabled: !c.disabled } })}
                onDelete={() => {
                  if (window.confirm(`Delete OAuth client "${c.name}"? Active tokens and consents will be revoked.`)) {
                    remove.mutate(c.id);
                  }
                }}
                busy={update.isPending || remove.isPending}
              />
            ))}
            {sortable.sorted.length === 0 && (
              <tr><td colSpan={7} className="muted">No clients registered yet.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

function NewClientSecret({ created, onDismiss }: {
  created: OAuthClientCreated;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState<"id" | "secret" | null>(null);
  const copy = async (text: string, which: "id" | "secret") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      window.setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard blocked — user can still select+copy manually */
    }
  };
  return (
    <div className="formstack">
      <p className="muted">
        Save the <strong>client_secret</strong> now — it's hashed in the database and
        won't be shown again. The <strong>client_id</strong> is recoverable from the
        list below at any time.
      </p>
      <div className="kv-grid">
        <span className="muted">client_id</span>
        <span style={{ display: "flex", gap: "0.5em", alignItems: "center" }}>
          <code className="code-sm">{created.client_id}</code>
          <button type="button" className="secondary" onClick={() => copy(created.client_id, "id")}>
            {copied === "id" ? "Copied" : "Copy"}
          </button>
        </span>
        <span className="muted">client_secret</span>
        <span style={{ display: "flex", gap: "0.5em", alignItems: "center" }}>
          <code className="code-sm">{created.client_secret}</code>
          <button type="button" className="secondary" onClick={() => copy(created.client_secret, "secret")}>
            {copied === "secret" ? "Copied" : "Copy"}
          </button>
        </span>
      </div>
      <button type="button" onClick={onDismiss}>I've saved the secret — done</button>
    </div>
  );
}

function ClientRow({
  client, canMutate, editing, editName, editUrls,
  onStartEdit, onCancelEdit, onChangeName, onChangeUrls, onSaveEdit,
  onToggleDisabled, onDelete, busy,
}: {
  client: OAuthClient;
  canMutate: boolean;
  editing: boolean;
  editName: string;
  editUrls: string;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeName: (v: string) => void;
  onChangeUrls: (v: string) => void;
  onSaveEdit: () => void;
  onToggleDisabled: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  if (editing) {
    return (
      <tr>
        <td><input value={editName} onChange={(e) => onChangeName(e.target.value)} /></td>
        <td><code className="code-sm">{client.clientId}</code></td>
        <td colSpan={3}>
          <textarea
            rows={3}
            style={{ width: "100%" }}
            value={editUrls}
            onChange={(e) => onChangeUrls(e.target.value)}
          />
        </td>
        <td>{client.disabled ? "Disabled" : "Active"}</td>
        <td>
          <button onClick={onSaveEdit} disabled={busy}>Save</button>
          <button className="secondary" onClick={onCancelEdit} disabled={busy}>Cancel</button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td>{client.name}</td>
      <td><code className="code-sm">{client.clientId}</code></td>
      <td>
        {client.redirectUrls.map((u, i) => (
          <div key={i}><code className="code-sm">{u}</code></div>
        ))}
      </td>
      <td>{client.creatorEmail ?? <span className="muted">—</span>}</td>
      <td>{new Date(client.createdAt).toLocaleDateString()}</td>
      <td>{client.disabled ? <span className="muted">Disabled</span> : "Active"}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        {canMutate ? (
          <>
            <button className="secondary" onClick={onStartEdit} disabled={busy}>Edit</button>
            <button className="secondary" onClick={onToggleDisabled} disabled={busy}>
              {client.disabled ? "Enable" : "Disable"}
            </button>
            <button className="danger" onClick={onDelete} disabled={busy}>Delete</button>
          </>
        ) : null}
      </td>
    </tr>
  );
}
